import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Printer } from "lucide-react";
import { ReceiptSheet } from "../ReceiptPage";
import { PortalHeader, NotificationStatus } from "./CitizenPay";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { api, downloadBlob, errMsg, fmtINR } from "../../lib/api";
import { ReceiptHiddenCard } from "../../components/ReceiptVisibilityNotice";   // Phase 169
import { originParam } from "../../components/TransferCertificate";   // Phase 208: QR base URL for the PDF

/**
 * Phase 118 — public receipt for a Citizen Portal payment (`/:code/pay/receipt/:token`).
 * The token is minted at payment time and is the only way to reach the receipt without a login;
 * this is the link that goes out in the SMS / WhatsApp confirmation.
 */
export default function PublicReceipt() {
  const { code, token } = useParams();
  const [data, setData] = useState(null);
  const [muni, setMuni] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/t/${code}/public/pay/receipt/${token}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
    api.get(`/t/${code}/public/pay/config`).then((r) => setMuni(r.data.municipality)).catch(() => {});
  }, [code, token]);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadBlob(`/t/${code}/public/pay/receipt/${token}/pdf?${originParam()}`, `receipt-${data?.receipt_number || token}.pdf`);
      toast.success("Receipt downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col" data-testid="public-receipt-page">
      <div className="no-print"><PortalHeader muni={muni} code={code} /></div>
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-2 no-print">
          <Button variant="outline" asChild data-testid="public-receipt-back">
            <Link to={`/${code}/pay`}><ArrowLeft className="h-4 w-4 mr-2" /> Payment portal</Link>
          </Button>
          {data && data.receipt_visible !== false && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.print()} data-testid="receipt-print-button"><Printer className="h-4 w-4 mr-2" /> Print</Button>
              <Button onClick={download} disabled={downloading} data-testid="citizen-receipt-download-button">
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
              </Button>
            </div>
          )}
        </div>
        {error ? (
          <Card className="shadow-sm"><CardContent className="p-8 text-center space-y-2" data-testid="public-receipt-error">
            <p className="font-medium">Receipt not found</p>
            <p className="text-sm text-muted-foreground">This link may be incomplete or the receipt may have been issued through a different channel.</p>
          </CardContent></Card>
        ) : !data ? (
          <Skeleton className="h-[520px] w-full rounded-xl" data-testid="public-receipt-skeleton" />
        ) : data.receipt_visible === false ? (
          /* Phase 169: the municipality has switched receipt viewing off for citizens — confirm the payment, withhold the document */
          <>
            <ReceiptHiddenCard receiptNumber={data.receipt_number} amount={data.receipt_data?.total_amount != null ? fmtINR(data.receipt_data.total_amount) : null}
              paymentDate={data.receipt_data?.payment_date || data.receipt_date} message={data.receipt_hidden_message} testId="public-receipt-hidden" />
            <Card className="shadow-sm no-print"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Confirmation messages</p>
              <NotificationStatus code={code} token={token} initial={data.notifications} />
            </CardContent></Card>
          </>
        ) : (
          <>
            <ReceiptSheet d={data.receipt_data} code={code} />
            <Card className="shadow-sm no-print"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">Confirmation messages</p>
              <NotificationStatus code={code} token={token} initial={data.notifications} />
            </CardContent></Card>
          </>
        )}
      </main>
    </div>
  );
}
